import { http, HttpResponse } from 'msw'
// Databricks delta table mock data (unity-catalog)
const unityCatalogs = [
    { name: 'main' },
    { name: 'analytics' }
];

const unitySchemas: Record<string, { name: string; catalogName: string }[]> = {
    main: [{ name: 'sales', catalogName: 'main' }, { name: 'marketing', catalogName: 'main' }],
    analytics: [{ name: 'ad_reporting', catalogName: 'analytics' }]
};

const unityTables: Record<string, any[]> = {
    'main.sales': [
        {
            name: 'customers', comment: 'Customer dimension', columns: [
                { name: 'customer_id', dataType: 'int', nullable: false },
                { name: 'customer_name', dataType: 'string', nullable: true },
                { name: 'country', dataType: 'string', nullable: true }
            ], row_count_estimate: 120000
        },
        {
            name: 'orders', comment: 'Orders fact', columns: [
                { name: 'order_id', dataType: 'int', nullable: false },
                { name: 'order_date', dataType: 'date', nullable: true },
                { name: 'amount', dataType: 'decimal', nullable: true }
            ], row_count_estimate: 450000
        }
    ],
    'main.marketing': [
        {
            name: 'campaigns', comment: 'Campaign info', columns: [
                { name: 'campaign_id', dataType: 'int', nullable: false },
                { name: 'campaign_name', dataType: 'string', nullable: true },
                { name: 'start_date', dataType: 'date', nullable: true }
            ], row_count_estimate: 900
        }
    ],
    'analytics.ad_reporting': [
        {
            name: 'impressions_daily', comment: 'Daily ad impressions', columns: [
                { name: 'date', dataType: 'date', nullable: false },
                { name: 'ad_group', dataType: 'string', nullable: false },
                { name: 'impressions', dataType: 'int', nullable: true }
            ], row_count_estimate: 73000
        }
    ]
};

function randomPreviewRows(columns: any[], limit: number) {
    const rows: Record<string, any>[] = [];
    for (let i = 0; i < limit; i++) {
        const row: Record<string, any> = {};
        columns.forEach(col => {
            const type = col.dataType;
            if (type.includes('int') || type === 'decimal') row[col.name] = Math.floor(Math.random() * 1000);
            else if (type === 'date') row[col.name] = `2025-01-${String(1 + i).padStart(2, '0')}`;
            else row[col.name] = `${col.name}_val_${i + 1}`;
        });
        rows.push(row);
    }
    return rows;
}

// Sample data objects for Data Admission
const sampleDataSources = [
    {
        id: 1,
        name: 'customer_data_2024.csv',
        type: 'CSV',
        size: '2.5 MB',
        recordCount: 1250,
        uploadedAt: '2024-01-15T10:30:00Z',
        status: 'processed'
    },
    {
        id: 2,
        name: 'inventory_data.xlsx',
        type: 'Excel',
        size: '1.8 MB',
        recordCount: 500,
        uploadedAt: '2024-01-22T09:15:00Z',
        status: 'processing'
    },
    {
        id: 3,
        name: 'sales_quarterly_report.csv',
        type: 'CSV',
        size: '3.1 MB',
        recordCount: 2100,
        uploadedAt: '2024-01-25T11:20:00Z',
        status: 'processed'
    },
    {
        id: 4,
        name: 'financial_data.xlsx',
        type: 'Excel',
        size: '4.7 MB',
        recordCount: 1800,
        uploadedAt: '2024-01-28T16:45:00Z',
        status: 'processed'
    }
]

export const handlers = [
    // Get all data objects
    http.get('/api/datasources', () => {
        return HttpResponse.json({
            success: true,
            data: sampleDataSources,
            total: sampleDataSources.length
        })
    }),

    // Upload data object
    http.post('/api/datasources/upload', async ({ request }) => {
        const formData = await request.formData()
        const file = formData.get('file') as File

        if (!file) {
            return HttpResponse.json(
                { success: false, message: 'No file provided' },
                { status: 400 }
            )
        }

        // Simulate file processing
        const fileExtension = file.name.split('.').pop()?.toLowerCase()
        const fileType = fileExtension === 'csv' ? 'CSV' :
            (fileExtension === 'xlsx' || fileExtension === 'xls') ? 'Excel' : 'Unknown'

        const newDataSource = {
            id: Math.max(...sampleDataSources.map(ds => ds.id)) + 1,
            name: file.name,
            type: fileType,
            size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
            recordCount: Math.floor(Math.random() * 1000) + 100, // Simulate record count
            uploadedAt: new Date().toISOString(),
            status: 'processing' as const
        }

        sampleDataSources.push(newDataSource)

        // Simulate processing completion after a delay
        setTimeout(() => {
            const dataSource = sampleDataSources.find(ds => ds.id === newDataSource.id)
            if (dataSource) {
                dataSource.status = 'processed'
            }
        }, 3000) // 3 seconds delay

        return HttpResponse.json({
            success: true,
            data: newDataSource,
            message: 'Data object uploaded successfully'
        }, { status: 201 })
    }),

    // Analyze file columns
    http.post('/api/datasources/analyze', async ({ request }) => {
        const formData = await request.formData()
        const file = formData.get('file') as File

        if (!file) {
            return HttpResponse.json(
                { success: false, message: 'No file provided' },
                { status: 400 }
            )
        }

        // Mock column analysis based on file type
        const fileExtension = file.name.split('.').pop()?.toLowerCase()
        let mockColumns: Array<{ name: string, detectedType: string, sampleValues: string[] }> = []

        if (fileExtension === 'csv') {
            mockColumns = [
                { name: 'customer_id', detectedType: 'Integer', sampleValues: ['1001', '1002', '1003'] },
                { name: 'customer_name', detectedType: 'String', sampleValues: ['John Doe', 'Jane Smith', 'Bob Johnson'] },
                { name: 'email', detectedType: 'String', sampleValues: ['john@email.com', 'jane@email.com', 'bob@email.com'] },
                { name: 'registration_date', detectedType: 'Date', sampleValues: ['2024-01-15', '2024-01-20', '2024-01-25'] },
                { name: 'is_active', detectedType: 'Boolean', sampleValues: ['true', 'false', 'true'] },
                { name: 'total_orders', detectedType: 'Integer', sampleValues: ['15', '8', '23'] }
            ]
        } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
            mockColumns = [
                { name: 'product_id', detectedType: 'Integer', sampleValues: ['P001', 'P002', 'P003'] },
                { name: 'product_name', detectedType: 'String', sampleValues: ['Laptop', 'Mouse', 'Keyboard'] },
                { name: 'price', detectedType: 'Float', sampleValues: ['999.99', '29.99', '79.99'] },
                { name: 'category', detectedType: 'String', sampleValues: ['Electronics', 'Accessories', 'Accessories'] },
                { name: 'in_stock', detectedType: 'Boolean', sampleValues: ['true', 'true', 'false'] },
                { name: 'last_updated', detectedType: 'Date', sampleValues: ['2024-01-15', '2024-01-18', '2024-01-20'] }
            ]
        }

        // Add empty description to each column
        const columnsWithMetadata = mockColumns.map(col => ({
            ...col,
            description: '',
            selectedType: col.detectedType
        }))

        // Generate mock preview data
        const totalRows = Math.floor(Math.random() * 1000) + 100
        const previewData = []

        for (let i = 0; i < Math.min(5, totalRows); i++) {
            const row: Record<string, any> = {}
            mockColumns.forEach(col => {
                if (col.detectedType === 'Integer' || col.detectedType === 'Float') {
                    row[col.name] = Math.floor(Math.random() * 1000) + 1
                } else if (col.detectedType === 'Boolean') {
                    row[col.name] = Math.random() > 0.5
                } else if (col.detectedType === 'Date') {
                    const randomDate = new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1)
                    row[col.name] = randomDate.toISOString().split('T')[0]
                } else {
                    // String type
                    const sampleIndex = Math.floor(Math.random() * col.sampleValues.length)
                    row[col.name] = col.sampleValues[sampleIndex] + (i > 0 ? ` ${i}` : '')
                }
            })
            previewData.push(row)
        }

        // Removed artificial delay: respond immediately to align with real-time local parsing behavior.

        return HttpResponse.json({
            success: true,
            data: {
                fileName: file.name,
                fileSize: file.size,
                columns: columnsWithMetadata,
                recordCount: totalRows,
                totalRows: totalRows,
                previewData: previewData
            }
        })
    }),

    // Delete data object
    http.delete('/api/datasources/:id', ({ params }) => {
        const { id } = params
        const dataSourceIndex = sampleDataSources.findIndex(ds => ds.id === parseInt(id as string))

        if (dataSourceIndex === -1) {
            return HttpResponse.json(
                { success: false, message: 'Data object not found' },
                { status: 404 }
            )
        }

        sampleDataSources.splice(dataSourceIndex, 1)

        return HttpResponse.json({
            success: true,
            message: 'Data object deleted successfully'
        })
    })
    ,
    // Unity Catalog: catalogs
    http.get('/api/v1/unity-catalog/catalogs', ({ request }) => {
        const url = new URL(request.url);
        // Accept user_email param for parity with backend; ignored in mock data
        const userEmail = url.searchParams.get('user_email');
        // Optionally could filter per user in future; presently return static catalogs
        return HttpResponse.json({ catalogs: unityCatalogs, user_email: userEmail });
    }),
    // Unity Catalog: schemas
    http.get('/api/v1/unity-catalog/schemas', ({ request }) => {
        const url = new URL(request.url);
        const catalog = url.searchParams.get('catalog');
        const userEmail = url.searchParams.get('user_email');
        const schemas = catalog ? (unitySchemas[catalog] || []) : [];
        return HttpResponse.json({ schemas, user_email: userEmail });
    }),
    // Unity Catalog: tables
    http.get('/api/v1/unity-catalog/tables', ({ request }) => {
        const url = new URL(request.url);
        const catalog = url.searchParams.get('catalog');
        const schema = url.searchParams.get('schema');
        const userEmail = url.searchParams.get('user_email');
        const key = catalog && schema ? `${catalog}.${schema}` : '';
        const tables = key ? (unityTables[key] || []) : [];
        return HttpResponse.json({ tables, user_email: userEmail });
    }),
    // Databricks preview placeholder
    http.get('/api/v1/databricks/preview', ({ request }) => {
        const url = new URL(request.url);
        const fullName = url.searchParams.get('table_full_name');
        const limitParam = url.searchParams.get('limit');
        const limit = limitParam ? parseInt(limitParam, 10) : 5;
        if (!fullName) return HttpResponse.json({ columns: [], rows: [] });
        const parts = fullName.split('.');
        if (parts.length !== 3) return HttpResponse.json({ columns: [], rows: [] });
        const [catalog, schema, table] = parts;
        const key = `${catalog}.${schema}`;
        const tables = unityTables[key] || [];
        const meta = tables.find(t => t.name === table);
        const columns = meta?.columns || [];
        return HttpResponse.json({ columns, rows: randomPreviewRows(columns, limit), total_rows: meta?.row_count_estimate || 0 });
    }),

    // Feedback API Mock Handlers
    // POST /api/v1/feedback - Create basic feedback
    http.post('*/api/v1/feedback', async ({ request }) => {
        const body = await request.json() as any;


        // Simulate successful feedback creation
        const response = {
            id: `feedback_${Date.now()}_${Math.random().toString(36).substring(2)}`,
            user_email: body.user_email,
            related_entity_type: body.related_entity_type || null,
            related_entity_id: body.related_entity_id || null,
            like_dislike: body.like_dislike,
            feedback: body.feedback,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            related_entity_name: null
        };


        return HttpResponse.json(response, { status: 201 });
    }),

    // Legacy report-specific feedback endpoint - now redirects to unified endpoint
    // @deprecated - Use the unified /api/v1/feedback endpoint with related_entity_type='report'
    http.post('*/api/v1/feedback/reports', async ({ request }) => {
        console.warn('⚠️ DEPRECATED: /api/v1/feedback/reports endpoint is deprecated. Use /api/v1/feedback with related_entity_type="report" instead.');

        const body = await request.json() as any;


        // Convert to unified format and process through main endpoint
        const unifiedBody = {
            user_email: body.user_email,
            related_entity_type: 'report',
            related_entity_id: body.report_id,
            like_dislike: body.like_dislike,
            feedback: body.feedback
        };

        // Simulate successful feedback creation with unified format
        const response = {
            id: `feedback_${Date.now()}_${Math.random().toString(36).substring(2)}`,
            user_email: unifiedBody.user_email,
            related_entity_type: unifiedBody.related_entity_type,
            related_entity_id: unifiedBody.related_entity_id,
            like_dislike: unifiedBody.like_dislike,
            feedback: unifiedBody.feedback,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            related_entity_name: 'Sample Report Name'
        };


        return HttpResponse.json(response, { status: 201 });
    }),

    // GET /api/v1/feedback - List feedback (supports both user and admin requests)
    // Match both /api/v1/feedback and /api/v1/feedback/ patterns
    http.get('*/api/v1/feedback', ({ request }) => {
        const url = new URL(request.url);
        const userEmail = url.searchParams.get('user_email');

        // Admin request - return all feedback data
        // Minimal mock feedback data - just two examples
        const mockFeedbackData = [
            {
                id: 'fb_001',
                user_email: 'john.doe@company.com',
                report_id: null, // General feedback
                like_dislike: true,
                feedback: {
                    rating: 5,
                    comment: 'The data analysis feature is excellent! Very intuitive interface and detailed reports.'
                },
                created_at: '2026-02-15T10:30:00Z',
                updated_at: '2026-02-15T10:30:00Z'
            },
            {
                id: 'fb_002',
                user_email: 'sarah.wilson@business.com',
                report_id: 'rpt_f452906d-90dd-471f-a72c-4c924ef88b5d',
                like_dislike: false,
                feedback: {
                    rating: 2,
                    comment: 'The report generation is too slow. Takes over 5 minutes for basic analysis.'
                },
                created_at: '2026-02-15T14:45:00Z',
                updated_at: '2026-02-15T14:45:00Z'
            }
        ];

        const response = {
            feedback: mockFeedbackData,
            total: mockFeedbackData.length
        };

        return HttpResponse.json(response, { status: 200 });
    }),

    // GET /api/v1/feedback/admin/all - Admin endpoint for fetching all feedback
    http.get('*/api/v1/feedback/admin/all', ({ request }) => {
        const url = new URL(request.url);

        // Admin request - return all feedback data
        // Minimal mock feedback data - just two examples
        const mockFeedbackData = [
            {
                id: 'fb_001',
                user_email: 'john.doe@company.com',
                related_entity_type: null,
                related_entity_id: null,
                like_dislike: true,
                feedback: {
                    rating: 5,
                    comment: 'The data analysis feature is excellent! Very intuitive interface and detailed reports.',
                    category: 'general',
                    severity: 'medium',
                    tags: ['ui', 'analysis']
                },
                created_at: '2024-01-15T10:30:00Z',
                updated_at: '2024-01-15T10:30:00Z',
                related_entity_name: null
            },
            {
                id: 'fb_002',
                user_email: 'jane.smith@company.com',
                related_entity_type: 'report',
                related_entity_id: 'report_123',
                like_dislike: false,
                feedback: {
                    rating: 2,
                    comment: 'Report generation is too slow and sometimes fails.',
                    category: 'feedback_with_reports',
                    severity: 'high',
                    tags: ['performance', 'reliability']
                },
                created_at: '2024-01-14T14:22:00Z',
                updated_at: '2024-01-14T14:22:00Z',
                related_entity_name: 'Q4 Analysis Report'
            }
        ];

        const response = {
            feedback: mockFeedbackData,
            total_count: mockFeedbackData.length,
            limit: 50,
            offset: 0
        };

        return HttpResponse.json(response, { status: 200 });
        return HttpResponse.json({ error: 'Admin email is required' }, { status: 400 });
    }),

    // Admin Failed Jobs endpoint
    http.get('/api/v1/data-analysis/data-diagnosis/admin/failed-jobs', ({ request }) => {
        const url = new URL(request.url);
        const limit = parseInt(url.searchParams.get('limit') ?? '20', 10);
        const offset = parseInt(url.searchParams.get('offset') ?? '0', 10);

        const mockJobs = [
            {
                id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
                user_email: 'jane.doe@example.com',
                source_type: 'databricks',
                source_identifier: 'catalog.schema.sales_data',
                dataset_name: 'Q4 Sales Report',
                status: 'FAILED',
                progress: 45,
                current_phase: 'reasoning',
                error_message: 'Data processing temporarily unavailable',
                created_at: '2026-05-14T10:30:00Z',
                updated_at: '2026-05-14T10:32:15Z',
                completed_at: '2026-05-14T10:32:15Z'
            },
            {
                id: 'b2c3d4e5-f6a7-8901-bcde-f12345678901',
                user_email: 'john.smith@example.com',
                source_type: 'file',
                source_identifier: 'inventory_check.csv',
                dataset_name: 'Inventory Check',
                status: 'CANCELLED',
                progress: 10,
                current_phase: 'ingestion',
                error_message: null,
                created_at: '2026-05-14T09:15:00Z',
                updated_at: '2026-05-14T09:15:45Z',
                completed_at: '2026-05-14T09:15:45Z'
            },
            {
                id: 'c3d4e5f6-a7b8-9012-cdef-234567890123',
                user_email: 'admin@example.com',
                source_type: 'file',
                source_identifier: 'monthly_revenue.xlsx',
                dataset_name: 'Monthly Revenue',
                status: 'FAILED',
                progress: 80,
                current_phase: 'report',
                error_message: 'Data analysis error occurred',
                created_at: '2026-05-13T14:20:00Z',
                updated_at: '2026-05-13T14:25:30Z',
                completed_at: '2026-05-13T14:25:30Z'
            },
            {
                id: 'd4e5f6a7-b8c9-0123-def0-345678901234',
                user_email: 'analyst@example.com',
                source_type: 'databricks',
                source_identifier: 'analytics.reporting.user_metrics',
                dataset_name: null,
                status: 'FAILED',
                progress: 20,
                current_phase: 'profiling',
                error_message: 'Processing timeout - please try again later',
                created_at: '2026-05-12T08:45:00Z',
                updated_at: '2026-05-12T08:55:00Z',
                completed_at: '2026-05-12T08:55:00Z'
            }
        ];

        const paged = mockJobs.slice(offset, offset + limit);

        return HttpResponse.json({
            jobs: paged,
            total_count: mockJobs.length,
            limit,
            offset
        }, { status: 200 });
    }),
]